import React, { useMemo, useCallback, useContext, useEffect, useRef } from 'react';
import { Radio, Space } from 'antd';
import type { RadioGroupProps as AntRadioGroupProps } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { parseChangeAction, createCommand } from '../../base/actionParser';
import { useFieldValidation } from '../../base/useFieldValidation';
import { deriveScopedComponentId, mergeStoreProps } from '../../base/storePropsUtils';
import { prepareAntdDomProps } from '../../utils/normalizeReactStyle';
import { useDictOptions, useRemoteOptions } from '../../base/hooks/useApiService';
import { findDictOptionByValue } from '../../config';
import { ValidateHelp } from '../../base/ValidateHelp';
import type { BaseComponentProps, DataSetConfig, ValidationDisplayConfig } from '../../types/common';
import { DataSetInheritContext, useResolvedDataSet } from '../../base/DataSetInheritContext';

interface RadioOption {
  label: string;
  value: string | number;
  disabled?: boolean;
}

// 选项配置接口
interface OptionsCfg {
  url?: string;
  codeKey?: string;
  labelKey?: string;
}

/**
 * AndRadio Props 接口
 */
export interface AndRadioProps extends Omit<AntRadioGroupProps, 'onChange' | 'options'>, BaseComponentProps, DataSetConfig, ValidationDisplayConfig {
  /** 选项列表 */
  options?: RadioOption[];
  /** 远程选项配置 */
  optionsCfg?: OptionsCfg;
  /** 变更动作 */
  changeAction?: string;
  /** 是否使用按钮样式 */
  buttonStyle?: 'outline' | 'solid';
  /** 是否使用按钮模式 */
  optionType?: 'default' | 'button';
  /** 校验规则 */
  rules?: any[];
  /** 失焦时校验 */
  validateOnBlur?: boolean;
  /** 修改时校验 */
  validateOnChange?: boolean;
  /** 自定义变更处理函数 */
  onChange?: (value: any) => void;
  /** 字典编码 */
  dictCode?: string;
  /** 远程数据URL（自定义属性） */
  remoteUrl?: string;
  /** 排列方向 */
  direction?: 'horizontal' | 'vertical';
  /** 当前未选中时，options 有值后是否自动选中第一个 */
  autoSelectFirst?: boolean;
  /** 迭代器内当前行序号，供 propsStore 隔离与动作参数使用 */
  index?: number;
}

/**
 * AndRadio 组件
 * 封装 Ant Design Radio.Group，支持数据绑定和动作解析
 */
export const AndRadio: React.FC<AndRadioProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  options = [],
  optionsCfg,
  changeAction,
  buttonStyle,
  optionType = 'default',
  rules,
  validateOnBlur,
  validateOnChange,
  showValidateHelp = true,
  onChange,
  value: propValue,
  defaultValue,
  dictCode,
  index,
  direction: propDirection,
  remoteUrl: _remoteUrl,
  autoSelectFirst = false,
  ...restProps
}) => {
  const resolved = useResolvedDataSet(dataSet, dataSetField, sessionId);
  const effectiveDataSet = resolved.dataSet;
  const effectiveDataSetField = resolved.dataSetField;
  const effectiveSessionId = resolved.sessionId;

  const addCommand = useAndStore((s) => s.addCommand);
  const setDataSet = useAndStore((s) => s.setDataSet);
  const ctx = useContext(DataSetInheritContext);
  const effectiveIndex = index ?? ctx?.index;
  const effectiveParentIndex = ctx?.parentIndex;

  const dataSetKey = useMemo(
    () => getDataSetStoreKey(effectiveSessionId, effectiveDataSet),
    [effectiveSessionId, effectiveDataSet]
  );
  const derivedPropsComponentId = useMemo(
    () => (effectiveSessionId ? deriveScopedComponentId(id, ctx, index) : null),
    [effectiveSessionId, id, ctx?.parentIndex, ctx?.index, index]
  );
  const storeProps = useAndStore((s) =>
    derivedPropsComponentId
      ? s.propsStore[`${effectiveSessionId}_props`]?.[derivedPropsComponentId] ?? null
      : null
  );
  const storeValue = useAndStore((s) =>
    dataSetKey && effectiveDataSetField ? s.getDataSet(dataSetKey, effectiveDataSetField) : undefined
  );

  const { validate, help } = useFieldValidation({
    rules,
    dataSet: effectiveDataSet,
    dataSetField: effectiveDataSetField,
    sessionId: effectiveSessionId,
    id,
    develop,
  });

  const rawDisplayValue = useMemo(() => {
    if (storeValue !== undefined) return storeValue;
    if (storeProps?.value !== undefined) return storeProps.value;
    return propValue ?? defaultValue;
  }, [storeValue, storeProps?.value, propValue, defaultValue]);

  // 使用 dictCode 加载字典数据
  const { options: dictOptions } = useDictOptions(dictCode);

  // 使用 optionsCfg 加载远程数据
  const { options: remoteOptions } = useRemoteOptions(
    optionsCfg?.url,
    {
      valueKey: optionsCfg?.codeKey || 'value',
      labelKey: optionsCfg?.labelKey || 'label',
      immediate: !!optionsCfg?.url,
    }
  );

  // 最终选项: Store > 远程 > 字典 > Props
  const finalOptions = useMemo(() => {
    if (storeProps?.options) return storeProps.options;
    if (optionsCfg?.url && remoteOptions.length > 0) return remoteOptions;
    if (dictCode && dictOptions.length > 0) return dictOptions as RadioOption[];
    return options;
  }, [storeProps?.options, optionsCfg?.url, remoteOptions, dictCode, dictOptions, options]);

  // 字典 value 多为 string，实际数据可能为 number，需用选项中的 value 以保证 antd Radio 正确选中
  const displayValue = useMemo(() => {
    if (finalOptions.length === 0) return rawDisplayValue;
    const matched = findDictOptionByValue(finalOptions, rawDisplayValue);
    return matched?.value ?? rawDisplayValue;
  }, [rawDisplayValue, finalOptions]);

  const mergedProps = useMemo(
    () => mergeStoreProps(restProps as Record<string, any>, storeProps ?? undefined),
    [restProps, storeProps]
  );

  const radioDomProps = useMemo(
    () => prepareAntdDomProps(mergedProps as Record<string, unknown>),
    [mergedProps],
  );

  const handleChange = useCallback(
    (e: any) => {
      const value = e.target.value;
      if (onChange) {
        onChange(value);
        return;
      }
      if (dataSetKey && effectiveDataSetField) setDataSet(dataSetKey, effectiveDataSetField, value);
      if (validateOnChange) validate(value);
      if (changeAction && effectiveSessionId) {
        const { actionName, arg } = parseChangeAction(changeAction, value, {
          id,
          dataSet: effectiveDataSet,
          dataSetField: effectiveDataSetField,
          index: effectiveIndex,
          parentIndex: effectiveParentIndex,
        }, ctx);
        addCommand(effectiveSessionId, createCommand(actionName, arg));
      }
    },
    [
      onChange,
      dataSetKey,
      effectiveDataSetField,
      setDataSet,
      validateOnChange,
      validate,
      changeAction,
      effectiveSessionId,
      id,
      effectiveDataSet,
      effectiveDataSetField,
      effectiveIndex,
      effectiveParentIndex,
      addCommand,
      ctx,
    ]
  );

  const handleBlur = useCallback(() => {
    if (!validateOnBlur) return;
    validate(displayValue);
  }, [validateOnBlur, validate, displayValue]);

  const autoSelectEnabled = storeProps?.autoSelectFirst ?? autoSelectFirst;
  const isCurrentValueEmpty =
    rawDisplayValue === undefined || rawDisplayValue === null || rawDisplayValue === '';
  const autoSelectSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    if (!autoSelectEnabled || finalOptions.length === 0 || !isCurrentValueEmpty) {
      autoSelectSignatureRef.current = null;
      return;
    }

    const firstOption = finalOptions[0];
    if (!firstOption) return;

    const firstValue = firstOption.value;
    if (firstValue === undefined || firstValue === null || firstValue === '') return;

    const signature = `${dataSetKey ?? ''}:${effectiveDataSetField ?? ''}:${String(firstValue)}`;
    if (autoSelectSignatureRef.current === signature) return;
    autoSelectSignatureRef.current = signature;

    handleChange({ target: { value: firstValue } });
  }, [
    autoSelectEnabled,
    dataSetKey,
    effectiveDataSetField,
    finalOptions,
    handleChange,
    isCurrentValueEmpty,
  ]);

  // 优先使用 props 中的 direction，如果 restProps 中有（来自 attributes）也处理
  // 注意：之前的修改中从 restProps 解构了 direction，这里我们在参数中直接解构 propDirection
  // 并且 Space 组件提示 direction 已弃用，建议使用 orientation
  // 我们同时检查 propDirection 和 restProps 中的 direction (如果有遗漏)

  return (
    <div onBlur={handleBlur}>
      <Radio.Group
        {...radioDomProps}
        disabled={develop ? false : (radioDomProps.disabled as boolean | undefined)}
        value={displayValue}
        onChange={handleChange}
        buttonStyle={storeProps?.buttonStyle ?? buttonStyle}
        optionType={storeProps?.optionType ?? optionType}
      >
        {optionType === 'button' ? (
          finalOptions.map((option: RadioOption) => (
            <Radio.Button key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </Radio.Button>
          ))
        ) : (
          <Space 
            // @ts-ignore Space orientation prop might not be in types yet if using older antd types
            orientation={(storeProps?.direction ?? propDirection ?? 'horizontal') as any} 
            wrap
          >
            {finalOptions.map((option: RadioOption) => (
              <Radio key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </Radio>
            ))}
          </Space>
        )}
      </Radio.Group>
      <ValidateHelp help={help} showValidateHelp={showValidateHelp} />
    </div>
  );
};

export default AndRadio;
